/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import {
  ComponentTypeEnum,
  UserActionEnum,
} from '@hyphengroup/cms-components/utils/analytics/sendAnalytics';
import classNames from 'classnames';
import { useCallback } from 'react';
import { tr } from '../../helpers';
import { ProductRewardBadgeTranslations } from '../../helpers/productRecommendation.i18n';
import { sendProductAnalytics } from '../../helpers/sendAnalytics';
import { DEFAULT_TYPE, TYPE_CONFIGS } from './constants';
import { EditorsPickBody } from './EditorsPickBody';
import { getEditorPickItem, getHeaderTitle } from './helpers';
import { LegacyStandoutBody } from './LegacyStandoutBody';
import { StandoutShell } from './StandoutShell';
import { StandoutStaticText } from './types';
import { useEditorsPickViewModel } from './useEditorsPickViewModel';
import {
  ComponentStandoutTextBlock,
  CmsProduct,
  EnumStandoutTextBlockType,
} from '$/(cms)/_modules/cmsService/types';
import { useProductDetailModal } from '$/components/cms/helpers/useProductDetailModal';
import { TRANSLATIONS } from '$/components/cms/translations';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';
import { sessionAttributionTracker } from '$/modules/sessionAttributionTracking/sessionAttributionTracker';

export const StandoutTextBlock = (props: ComponentStandoutTextBlock) => {
  const context = useClientContext();
  const { lang } = context;
  const { openProductDetail, productDetailModal } = useProductDetailModal({
    componentUniqueKey: `StandoutTextBlock_${props.id}`,
  });
  const staticText = getClientTranslations(
    TRANSLATIONS,
    context,
  ) as StandoutStaticText;
  const rewardBadgeStaticText = getClientTranslations(
    ProductRewardBadgeTranslations,
    context,
  );

  const titleType = props.type || DEFAULT_TYPE;
  // Keep rendering resilient when CMS/service deploys a new enum value ahead of FE.
  const typeConfig = TYPE_CONFIGS[titleType] ?? TYPE_CONFIGS[DEFAULT_TYPE];
  const headerTitle = getHeaderTitle(titleType, staticText);
  const isPerspective =
    titleType === EnumStandoutTextBlockType.ExpertPerspective;
  const isEditorsPick = titleType === EnumStandoutTextBlockType.EditorsPick;

  const buttonClasses = classNames(
    'w-full !rounded-radius/button bg-buttons/base',
    typeConfig.buttonHoverClass,
    typeConfig.buttonToneClass,
  );

  const editorsPickModel = useEditorsPickViewModel({
    item: getEditorPickItem(props.editor_pick_product),
    lang,
    rewardBadgeStaticText,
  });

  const handleApplyClick = useCallback(
    (product: CmsProduct, applyLabel: string) => {
      sessionAttributionTracker.recordApplyClick();
      sendProductAnalytics({
        action: UserActionEnum.CLICK,
        componentTitle: `Editor's Pick/CTA_${applyLabel}`,
        componentType: ComponentTypeEnum.CARD,
        event: 'product_click_apply',
        index: '0',
        product,
      });
    },
    [],
  );

  const handleViewDetailsClick = useCallback(
    (product: CmsProduct, viewDetailsLabel: string) => {
      sendProductAnalytics({
        action: UserActionEnum.CLICK,
        componentTitle: `Editor's Pick/Footer_${viewDetailsLabel}`,
        componentType: ComponentTypeEnum.CARD,
        event: 'product_interaction',
        index: '0',
        product,
      });

      openProductDetail(product, null);
    },
    [openProductDetail],
  );

  if (isEditorsPick && !editorsPickModel.product) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[StandoutTextBlock:${props.id}] Editor's Pick is missing a valid product reference and will not render.`,
      );
    }
    return null;
  }

  return (
    <StandoutShell
      headerBgClass={typeConfig.headerBgClass}
      icon={typeConfig.icon}
      title={headerTitle}
      bodyGradient={typeConfig.bodyGradient}
      bodyClassName={classNames(
        'flex flex-col px-spacing/padding/x py-spacing/padding/y',
        {
          'gap-spacing/gap/y-sm': isEditorsPick,
          'gap-4 md:flex-row': !isEditorsPick,
        },
      )}
    >
      {isEditorsPick ? (
        <EditorsPickBody
          buttonTarget={props.button_target}
          content={editorsPickModel.item?.content}
          model={editorsPickModel}
          onApplyClick={() => {
            if (editorsPickModel.product) {
              handleApplyClick(
                editorsPickModel.product,
                editorsPickModel.applyLabel,
              );
            }
          }}
          onViewDetailsClick={() => {
            if (editorsPickModel.product) {
              handleViewDetailsClick(
                editorsPickModel.product,
                tr('spotlight_details', lang),
              );
            }
          }}
          viewDetailsLabel={tr('spotlight_details', lang)}
        />
      ) : (
        <LegacyStandoutBody
          author={props.author}
          buttonClasses={buttonClasses}
          buttonTarget={props.button_target}
          buttonText={props.button_text}
          buttonUrl={props.button_url}
          content={props.content}
          includeCta={props.include_cta}
          isPerspective={isPerspective}
        />
      )}
      {productDetailModal}
    </StandoutShell>
  );
};
